import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { useAuth } from './AuthContext';

const TickerContext = createContext();

// Sport categories for grouping
export const SPORT_CATEGORIES = [
  { id: 'all_basketball', label: '🏀 All Basketball', sport: 'basketball', isCategory: true },
  { id: 'all_football', label: '🏈 All Football', sport: 'football', isCategory: true },
  { id: 'all_baseball', label: '⚾ All Baseball', sport: 'baseball', isCategory: true },
  { id: 'all_hockey', label: '🏒 All Hockey', sport: 'hockey', isCategory: true },
  { id: 'all_soccer', label: '⚽ All Soccer', sport: 'soccer', isCategory: true },
  { id: 'all_tennis', label: '🎾 All Tennis', sport: 'tennis', isCategory: true },
  { id: 'all_combat', label: '🥊 All Combat Sports', sport: 'combat', isCategory: true },
  { id: 'all_golf', label: '⛳ All Golf', sport: 'golf', isCategory: true },
  { id: 'all_motorsports', label: '🏎️ All Motorsports', sport: 'motorsports', isCategory: true },
  { id: 'all_rugby', label: '🏉 All Rugby', sport: 'rugby', isCategory: true },
  { id: 'all_cricket', label: '🏏 All Cricket', sport: 'cricket', isCategory: true },
  { id: 'all_esports', label: '🎮 All Esports', sport: 'esports', isCategory: true },
];

// Individual league options - comprehensive list (matches oddsRefresh.js leagues)
export const AVAILABLE_TICKER_OPTIONS = [
  // ═══════════════════════════════════════════════════════════════════════════════
  // Basketball - All leagues
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'nba', label: 'NBA', sport: 'basketball' },
  { id: 'wnba', label: 'WNBA', sport: 'basketball' },
  { id: 'ncaab', label: 'NCAA Basketball', sport: 'basketball' },
  { id: 'ncaaw', label: 'NCAA Women\'s Basketball', sport: 'basketball' },
  { id: 'nba_g_league', label: 'NBA G League', sport: 'basketball' },
  { id: 'euroleague', label: 'EuroLeague', sport: 'basketball' },
  { id: 'eurocup', label: 'EuroCup', sport: 'basketball' },
  { id: 'australia_-_nbl', label: 'NBL Australia', sport: 'basketball' },
  { id: 'australia_-_wnbl', label: 'WNBL Australia', sport: 'basketball' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Football - All leagues
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'nfl', label: 'NFL', sport: 'football' },
  { id: 'ncaaf', label: 'NCAA Football', sport: 'football' },
  { id: 'cfl', label: 'CFL', sport: 'football' },
  { id: 'ufl', label: 'UFL', sport: 'football' },
  { id: 'xfl', label: 'XFL', sport: 'football' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Baseball - All leagues
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'mlb', label: 'MLB', sport: 'baseball' },
  { id: 'milb_aaa', label: 'MiLB (AAA)', sport: 'baseball' },
  { id: 'ncaa_baseball', label: 'NCAA Baseball', sport: 'baseball' },
  { id: 'kbo', label: 'KBO (Korea)', sport: 'baseball' },
  { id: 'npb', label: 'NPB (Japan)', sport: 'baseball' },
  { id: 'cpbl', label: 'CPBL (Taiwan)', sport: 'baseball' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Hockey - All leagues
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'nhl', label: 'NHL', sport: 'hockey' },
  { id: 'ahl', label: 'AHL', sport: 'hockey' },
  { id: 'khl', label: 'KHL', sport: 'hockey' },
  { id: 'shl', label: 'SHL (Sweden)', sport: 'hockey' },
  { id: 'liiga', label: 'Liiga (Finland)', sport: 'hockey' },
  { id: 'pwhl', label: 'PWHL (Women)', sport: 'hockey' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Soccer - Major leagues (top 20)
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'england_-_premier_league', label: 'Premier League', sport: 'soccer' },
  { id: 'spain_-_la_liga', label: 'La Liga', sport: 'soccer' },
  { id: 'germany_-_bundesliga', label: 'Bundesliga', sport: 'soccer' },
  { id: 'italy_-_serie_a', label: 'Serie A', sport: 'soccer' },
  { id: 'france_-_ligue_1', label: 'Ligue 1', sport: 'soccer' },
  { id: 'uefa_-_champions_league', label: 'Champions League', sport: 'soccer' },
  { id: 'uefa_-_europa_league', label: 'Europa League', sport: 'soccer' },
  { id: 'usa_-_major_league_soccer', label: 'MLS', sport: 'soccer' },
  { id: 'mexico_-_liga_mx', label: 'Liga MX', sport: 'soccer' },
  { id: 'usa_-_nwsl', label: 'NWSL (Women)', sport: 'soccer' },
  { id: 'england_-_championship', label: 'Championship', sport: 'soccer' },
  { id: 'england_-_wsl', label: 'WSL (Women)', sport: 'soccer' },
  { id: 'brazil_-_serie_a', label: 'Serie A (Brazil)', sport: 'soccer' },
  { id: 'argentina_-_primera_division', label: 'Primera División', sport: 'soccer' },
  { id: 'netherlands_-_eredivisie', label: 'Eredivisie', sport: 'soccer' },
  { id: 'portugal_-_primeira_liga', label: 'Primeira Liga', sport: 'soccer' },
  { id: 'scotland_-_premiership', label: 'Scottish Prem', sport: 'soccer' },
  { id: 'australia_-_a_league', label: 'A-League', sport: 'soccer' },
  { id: 'saudi_arabia_-_pro_league', label: 'Saudi Pro League', sport: 'soccer' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Tennis - All tours
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'atp', label: 'ATP Tour', sport: 'tennis' },
  { id: 'wta', label: 'WTA Tour', sport: 'tennis' },
  { id: 'atp_challenger', label: 'ATP Challenger', sport: 'tennis' },
  { id: 'itf_men', label: 'ITF Men', sport: 'tennis' },
  { id: 'itf_women', label: 'ITF Women', sport: 'tennis' },
  { id: 'grand_slam', label: 'Grand Slams', sport: 'tennis' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Combat Sports - All promotions
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'ufc', label: 'UFC', sport: 'combat' },
  { id: 'pfl', label: 'PFL', sport: 'combat' },
  { id: 'bellator', label: 'Bellator', sport: 'combat' },
  { id: 'one_championship', label: 'ONE Championship', sport: 'combat' },
  { id: 'boxing', label: 'Boxing', sport: 'combat' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Golf - All tours
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'pga', label: 'PGA Tour', sport: 'golf' },
  { id: 'lpga', label: 'LPGA Tour', sport: 'golf' },
  { id: 'liv', label: 'LIV Golf', sport: 'golf' },
  { id: 'dp_world_tour', label: 'DP World Tour', sport: 'golf' },
  { id: 'pga_champions', label: 'PGA Champions', sport: 'golf' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Motorsports - All series
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'formula_1', label: 'Formula 1', sport: 'motorsports' },
  { id: 'nascar_cup', label: 'NASCAR Cup', sport: 'motorsports' },
  { id: 'nascar_xfinity', label: 'NASCAR Xfinity', sport: 'motorsports' },
  { id: 'indycar', label: 'IndyCar', sport: 'motorsports' },
  { id: 'motogp', label: 'MotoGP', sport: 'motorsports' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Rugby - All leagues
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'nrl', label: 'NRL', sport: 'rugby' },
  { id: 'nrlw', label: 'NRLW (Women)', sport: 'rugby' },
  { id: 'super_league', label: 'Super League', sport: 'rugby' },
  { id: 'six_nations', label: 'Six Nations', sport: 'rugby' },
  { id: 'premiership_rugby', label: 'Premiership Rugby', sport: 'rugby' },
  { id: 'super_rugby', label: 'Super Rugby', sport: 'rugby' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Cricket - All tournaments
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'ipl', label: 'IPL', sport: 'cricket' },
  { id: 'big_bash', label: 'Big Bash', sport: 'cricket' },
  { id: 'bbl_women', label: 'BBL Women', sport: 'cricket' },
  { id: 'the_hundred', label: 'The Hundred', sport: 'cricket' },
  { id: 'psl', label: 'PSL (Pakistan)', sport: 'cricket' },
  { id: 'sa20', label: 'SA20', sport: 'cricket' },
  { id: 'test_matches', label: 'Test Matches', sport: 'cricket' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Esports - Major titles
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'cs2', label: 'CS2', sport: 'esports' },
  { id: 'league_of_legends', label: 'League of Legends', sport: 'esports' },
  { id: 'dota2', label: 'Dota 2', sport: 'esports' },
  { id: 'valorant', label: 'Valorant', sport: 'esports' },
  { id: 'call_of_duty', label: 'Call of Duty', sport: 'esports' },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // Other Sports
  // ═══════════════════════════════════════════════════════════════════════════════
  { id: 'afl', label: 'AFL', sport: 'aussie_rules' },
  { id: 'aflw', label: 'AFLW (Women)', sport: 'aussie_rules' },
  { id: 'nll', label: 'NLL (Lacrosse)', sport: 'lacrosse' },
  { id: 'pll', label: 'PLL (Lacrosse)', sport: 'lacrosse' },
  { id: 'pdc', label: 'PDC Darts', sport: 'darts' },
];

// Default ticker preferences - most popular leagues
// IDs must match AVAILABLE_TICKER_OPTIONS and the values stored in odds_cache.league
export const DEFAULT_TICKER_LEAGUES = [
  'nba', 'wnba', 'nfl', 'mlb', 'nhl', 'ncaab', 'ncaaf',
  'usa_-_major_league_soccer', 'england_-_premier_league', 'uefa_-_champions_league',
  'atp', 'wta', 'ufc'
];

// Name format options for ticker
export const TICKER_NAME_FORMATS = [
  { id: 'full', label: 'Full Names', example: 'Cleveland Cavaliers, Carlos Alcaraz' },
  { id: 'short', label: 'Short Names', example: 'Cavaliers, Alcaraz' },
  { id: 'abbreviated', label: 'Abbreviated', example: 'CLE, C. Alcaraz' },
];

// Helper to get all leagues for a sport category
export const getLeaguesBySport = (sport) => {
  if (sport === 'combat') {
    return AVAILABLE_TICKER_OPTIONS.filter(opt => opt.sport === 'combat').map(opt => opt.id);
  }
  return AVAILABLE_TICKER_OPTIONS.filter(opt => opt.sport === sport).map(opt => opt.id);
};

export const useTicker = () => {
  const context = useContext(TickerContext);
  if (!context) {
    throw new Error('useTicker must be used within a TickerProvider');
  }
  return context;
};

// Helper to get functions base URL
const getFunctionsUrl = () => {
  const isLocalhost = Platform.OS === 'web' && 
    (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
  
  // ⭐ Only use emulator if explicitly enabled via env var or query param
  const useEmulator = isLocalhost && (
    (typeof process !== 'undefined' && process?.env?.USE_EMULATOR === 'true') ||
    (typeof window !== 'undefined' && window.location?.search?.includes('emulator=true'))
  );
  
  if (useEmulator) {
    return 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1';
  }
  
  return (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
    || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
  );
};

export const TickerProvider = ({ children }) => {
  const { userProfile } = useAuth();
  
  // Ticker default visibility (persisted to localStorage - controlled by settings page)
  const [isTickerEnabled, setIsTickerEnabled] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerEnabled');
      return saved === null ? true : saved === 'true'; // Default to enabled
    }
    return true;
  });
  
  // Ticker collapsed state (session only - controlled by chevron on ticker)
  const [isTickerCollapsed, setIsTickerCollapsed] = useState(false);
  
  // User's league/sport preferences for ticker (from profile or localStorage)
  const [tickerLeagues, setTickerLeagues] = useState(() => {
    // First check localStorage for quick initial render
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerLeagues');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {}
      }
    }
    return DEFAULT_TICKER_LEAGUES;
  });
  
  // Name format preference: 'full', 'short', or 'abbreviated'
  const [tickerNameFormat, setTickerNameFormat] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerNameFormat');
      if (saved) return saved;
    }
    return 'short'; // Default to short names (e.g., "Cavaliers" instead of "Cleveland Cavaliers")
  });
  
  // ⭐ SGP Indicator setting - show when games have cached SGPs
  const [showTickerSGPIndicator, setShowTickerSGPIndicator] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerSGPIndicator');
      return saved === null ? true : saved === 'true'; // Default on when unset
    }
    return true; // Default to on - show SGP badges when available
  });
  
  // ⭐ SGP-only mode - only show games with cached SGPs in ticker
  const [tickerSGPOnly, setTickerSGPOnly] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerSGPOnly');
      return saved === 'true';
    }
    return false; // Default to off
  });
  
  // 🔴 Live filter mode — session-only, shows only in-progress games in the ticker

  // 🏥 Injury Ticker setting
  const [isInjuryTickerEnabled, setIsInjuryTickerEnabled] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('injuryTickerEnabled');
      return saved === null ? true : saved === 'true'; // Default to ON
    }
    return true;
  });

  // 🏥 Injury ticker league filter — null means "all leagues"
  const [injuryTickerLeagues, setInjuryTickerLeagues] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const saved = window.localStorage.getItem('injuryTickerLeagues');
        return saved ? JSON.parse(saved) : null;
      } catch { return null; }
    }
    return null;
  });

  // 🏥 Injury ticker minimum severity — 'all' | 'questionable_up' | 'doubtful_up' | 'out_only'
  const [injuryMinSeverity, setInjuryMinSeverity] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('injuryMinSeverity');
      return saved || 'all';
    }
    return 'all';
  });

  // 🏥 Injury ticker grouping — 'fixture' shows both teams together, 'team' splits per team
  const [injuryGroupBy, setInjuryGroupBy] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('injuryGroupBy');
      return saved || 'fixture';
    }
    return 'fixture';
  });
  
  // ⭐ Live Scores setting - show live scores in ticker
  const [showLiveScores, setShowLiveScores] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('tickerLiveScores');
      return saved === null ? true : saved === 'true'; // Default to ON
    }
    return true;
  });
  
  // Live scores data { fixture_id: { home_score, away_score, period, clock, status } }
  const [liveScores, setLiveScores] = useState({});
  const liveScoresFetchingRef = useRef(false);
  
  // SGP cache status for fixtures { fixture_id: boolean }
  // ⭐ Hydrate immediately from localStorage so badges are visible on first render
  const [sgpCacheStatus, setSgpCacheStatus] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const saved = window.localStorage.getItem('sgpCacheStatus');
      if (saved) {
        const { status, timestamp } = JSON.parse(saved);
        // Valid for 45 minutes — badges are stable between short sessions
        if (Date.now() - timestamp < 45 * 60 * 1000 && status) return status;
      }
    } catch {}
    return {};
  });
  // Ref mirror so fetchSGPCacheStatus reads current state without needing it as a dep
  const sgpCacheStatusRef = useRef(sgpCacheStatus);
  useEffect(() => { sgpCacheStatusRef.current = sgpCacheStatus; }, [sgpCacheStatus]);
  const sgpFetchingRef = useRef(false);
  // ⭐ v1.20.35: Per-fixture verification timestamp so badges can be re-verified
  // periodically even after landing on `true`. Without this, a badge stuck `true`
  // (from localStorage hydration or earlier session) is never re-checked, so
  // ticker clicks for now-empty caches waste 60-90s on live SGP generation.
  const sgpVerifiedAtRef = useRef((() => {
    if (typeof window === 'undefined') return {};
    try {
      const saved = window.localStorage.getItem('sgpCacheVerifiedAt');
      if (saved) return JSON.parse(saved) || {};
    } catch {}
    return {};
  })());
  const SGP_BADGE_VERIFY_TTL_MS = 15 * 60 * 1000; // re-verify every 15 minutes
  
  // Sync ticker leagues from user profile when it loads
  const profileSyncedRef = useRef(false);
  const needsRefetchRef = useRef(false); // Flag to trigger refetch after profile sync
  useEffect(() => {
    if (userProfile?.preferences?.tickerLeagues && !profileSyncedRef.current) {
      setTickerLeagues(userProfile.preferences.tickerLeagues);
      profileSyncedRef.current = true;
      needsRefetchRef.current = true; // Mark that we need to refetch
      // Also save to localStorage for faster initial load next time
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tickerLeagues', JSON.stringify(userProfile.preferences.tickerLeagues));
      }
    }
    if (userProfile?.preferences?.tickerNameFormat) {
      setTickerNameFormat(userProfile.preferences.tickerNameFormat);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tickerNameFormat', userProfile.preferences.tickerNameFormat);
      }
    }
  }, [userProfile?.preferences?.tickerLeagues, userProfile?.preferences?.tickerNameFormat]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // INSTANT LOAD: Use localStorage cache for immediate display
  // ═══════════════════════════════════════════════════════════════════════════
  const getCachedFixtures = () => {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
      const cached = window.localStorage.getItem('tickerFixturesCache');
      if (cached) {
        const { fixtures: cachedFixtures, timestamp, reference_time: cachedRefTime } = JSON.parse(cached);
        // Cache valid for 10 minutes
        if (Date.now() - timestamp < 10 * 60 * 1000 && Array.isArray(cachedFixtures)) {
          // console.log('[Ticker] ⚡ Using cached fixtures:', cachedFixtures.length);
          return { fixtures: cachedFixtures, referenceTime: cachedRefTime || null };
        }
      }
    } catch (e) {
      console.warn('[Ticker] Cache read error:', e);
    }
    return null;
  };
  
  const saveCachedFixtures = (fixturesData, referenceTimeOverride) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const refTime = referenceTimeOverride !== undefined ? referenceTimeOverride : referenceTime;
      // Only cache essential fields to keep storage small
      const minimalFixtures = fixturesData.slice(0, 200).map(f => ({
        fixture_id: f.fixture_id,
        id: f.id,
        home_team: f.home_team,
        away_team: f.away_team,
        league: f.league,
        sport: f.sport,
        start_date: f.start_date,
        home_logo: f.home_logo,
        away_logo: f.away_logo,
      }));
      window.localStorage.setItem('tickerFixturesCache', JSON.stringify({
        fixtures: minimalFixtures,
        timestamp: Date.now(),
        // frozen clock on next mount instead of real Date.now() (which would mark
        // all frozen games as 'ended' and empty the ticker). Cleared when live.
        reference_time: refTime ?? null
      }));
    } catch (e) {
      console.warn('[Ticker] Cache write error:', e);
    }
  };

  // ⭐ INSTANT: Initialize with cached fixtures if available
  const initialCache = getCachedFixtures();
  const initialFixtures = initialCache?.fixtures || null;

  // Fixtures data - split into initial (fast) and progressive batches
  const [fixtures, setFixtures] = useState(initialFixtures || []);
  const [rawFixtures, setRawFixtures] = useState(initialFixtures || []); // Store unfiltered fixtures for re-filtering
  const [isLoading, setIsLoading] = useState(!initialFixtures); // ⭐ Not loading if we have cache!
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadProgress, setLoadProgress] = useState(initialFixtures ? 1 : 0); // Start at 1 if cached
  const [error, setError] = useState(null);
  const lastFetchedRef = useRef(null);
  const fetchingRef = useRef(false);
  const fullFetchDoneRef = useRef(false);
  
  // Save ticker preference to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerEnabled', String(isTickerEnabled));
    }
  }, [isTickerEnabled]);
  
  // Update ticker leagues preference
  const updateTickerLeagues = useCallback((leagues) => {
    setTickerLeagues(leagues);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerLeagues', JSON.stringify(leagues));
    }
  }, []);
  
  // Update ticker name format preference
  const updateTickerNameFormat = useCallback((format) => {
    setTickerNameFormat(format);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerNameFormat', format);
    }
  }, []);
  
  // ⭐ Update SGP indicator preference
  const updateShowTickerSGPIndicator = useCallback((enabled) => {
    setShowTickerSGPIndicator(enabled);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerSGPIndicator', String(enabled));
    }
  }, []);
  
  // 🏥 Update injury ticker preference
  const updateInjuryTickerEnabled = useCallback((enabled) => {
    setIsInjuryTickerEnabled(enabled);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('injuryTickerEnabled', String(enabled));
    }
  }, []);

  // 🏥 Update injury ticker league filter (null = all)
  const updateInjuryTickerLeagues = useCallback((leagues) => {
    setInjuryTickerLeagues(leagues);
    if (typeof window !== 'undefined' && window.localStorage) {
      if (leagues === null) {
        window.localStorage.removeItem('injuryTickerLeagues');
      } else {
        window.localStorage.setItem('injuryTickerLeagues', JSON.stringify(leagues));
      }
    }
  }, []);

  // 🏥 Update injury ticker minimum severity filter
  const updateInjuryMinSeverity = useCallback((severity) => {
    setInjuryMinSeverity(severity);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('injuryMinSeverity', severity);
    }
  }, []);

  // 🏥 Update injury ticker group-by setting
  const updateInjuryGroupBy = useCallback((groupBy) => {
    setInjuryGroupBy(groupBy);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('injuryGroupBy', groupBy);
    }
  }, []);
  
  // ⭐ Update live scores preference
  const updateShowLiveScores = useCallback((enabled) => {
    setShowLiveScores(enabled);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerLiveScores', String(enabled));
    }
  }, []);
  
  // ⭐ Fetch live scores for fixtures
  const fetchLiveScores = useCallback(async (fixtureList) => {
    if (liveScoresFetchingRef.current || !showLiveScores) return;
    if (!fixtureList || fixtureList.length === 0) return;
    
    liveScoresFetchingRef.current = true;
    
    try {
      const functionsUrl = getFunctionsUrl();
      
      // Only fetch scores for games that might be live (started within last 4 hours)
      const now = Date.now();
      const fourHoursAgo = now - (4 * 60 * 60 * 1000);
      
      const potentiallyLiveFixtures = fixtureList.filter(f => {
        let startTime = null;
        if (f.start_date) {
          if (f.start_date._seconds) startTime = f.start_date._seconds * 1000;
          else if (f.start_date.seconds) startTime = f.start_date.seconds * 1000;
          else startTime = new Date(f.start_date).getTime();
        }
        // Game is potentially live if it started in the past 4 hours
        return startTime && startTime < now && startTime > fourHoursAgo;
      });
      
      if (potentiallyLiveFixtures.length === 0) {
        liveScoresFetchingRef.current = false;
        return;
      }
      
      const fixtureIds = potentiallyLiveFixtures.map(f => f.fixture_id).filter(Boolean);
      
      const response = await fetch(
        `${functionsUrl}/getLiveScores?fixture_ids=${fixtureIds.join(',')}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.scores) {
          setLiveScores(prev => ({ ...prev, ...data.scores }));
        }
      }
    } catch (err) {
      console.error('[Ticker] Live scores fetch error:', err);
    } finally {
      liveScoresFetchingRef.current = false;
    }
  }, [showLiveScores]);
  
  // ⭐ Update SGP-only mode
  const updateTickerSGPOnly = useCallback((enabled) => {
    setTickerSGPOnly(enabled);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tickerSGPOnly', String(enabled));
    }
    // If enabling SGP-only, also enable the SGP indicator
    if (enabled && !showTickerSGPIndicator) {
      setShowTickerSGPIndicator(true);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tickerSGPIndicator', 'true');
      }
    }
  }, [showTickerSGPIndicator]);
  
  // ⭐ Background fetch SGP cache status for ticker fixtures.
  // Uses sgpCacheStatusRef (not state) so the callback never needs to re-create —
  // this prevents the useCallback recreating → useEffect firing → fetch loop.
  const fetchSGPCacheStatus = useCallback(async (fixturesList) => {
    if (!fixturesList || fixturesList.length === 0 || sgpFetchingRef.current) return;
    
    sgpFetchingRef.current = true;
    const functionsUrl = getFunctionsUrl();
    
    try {
      const batchSize = 5;
      // Read current status from ref so we always skip already-known fixtures
      const newStatus = { ...sgpCacheStatusRef.current };
      
      for (let i = 0; i < fixturesList.length; i += batchSize) {
        const batch = fixturesList.slice(i, i + batchSize);
        
        await Promise.all(
          batch.map(async (fixture) => {
            const fixtureId = fixture.fixture_id || fixture.id;
            
            // ⭐ v1.20.35: Re-verify `true` badges every SGP_BADGE_VERIFY_TTL_MS so
            // stale localStorage entries don't keep showing badges for fixtures whose
            // cache has expired/wiped. Skip only if recently verified (or false/unknown
            // are always re-checked anyway).
            if (newStatus[fixtureId] === true) {
              const lastChecked = sgpVerifiedAtRef.current[fixtureId] || 0;
              if (Date.now() - lastChecked < SGP_BADGE_VERIFY_TTL_MS) return;
            }
            
            try {
              const response = await fetch(`${functionsUrl}/getCachedSGPs?fixture_id=${fixtureId}&stats_only=true`);
              if (!response.ok) { newStatus[fixtureId] = false; return; }
              const data = await response.json();
              newStatus[fixtureId] = data.success && (data.stats?.total_sgps > 0 || Object.values(data.sgps || {}).some(a => Array.isArray(a) && a.length > 0));
              // Track verification time so we don't hammer the endpoint
              sgpVerifiedAtRef.current[fixtureId] = Date.now();
            } catch {
              newStatus[fixtureId] = false;
            }
          })
        );
        
        // Progressive update — badges appear as each batch resolves
        setSgpCacheStatus({ ...newStatus });
        
        // ⭐ Persist to localStorage after each batch so badges survive page reloads
        try {
          window.localStorage.setItem('sgpCacheStatus', JSON.stringify({
            status: newStatus,
            timestamp: Date.now(),
          }));
          // ⭐ v1.20.35: Persist verification timestamps so re-verify TTL survives reloads
          window.localStorage.setItem('sgpCacheVerifiedAt', JSON.stringify(sgpVerifiedAtRef.current));
        } catch {}
      }
    } catch (err) {
      console.warn('[Ticker] Error fetching SGP cache status:', err);
    } finally {
      sgpFetchingRef.current = false;
    }
  }, []); // ⭐ No deps — reads state via ref, so this is stable for the lifetime of the component
  
  // Toggle ticker visibility
  const toggleTicker = useCallback(() => {
    setIsTickerEnabled(prev => !prev);
  }, []);
  
  const enableTicker = useCallback(() => {
    setIsTickerEnabled(true);
  }, []);
  
  const disableTicker = useCallback(() => {
    setIsTickerEnabled(false);
  }, []);
  
  // ⭐ Freeze-mode reference time — when Archive Mode is ON, the backend returns
  // `reference_time` (the freeze timestamp). We use it instead of Date.now() for
  // all time-based filtering so cached fixtures aren't dropped as "past/ended."
  // Restored from localStorage on mount so cached fixtures render correctly
  // before the first network response arrives.
  const [referenceTime, setReferenceTime] = useState(initialCache?.referenceTime || null);

  // Helper to filter fixtures by time relevance
  const filterByTimeRelevance = (fixturesData, referenceTimeOverride = null) => {
    const ref = referenceTimeOverride || referenceTime;
    const now = ref ? new Date(ref).getTime() : Date.now();
    const twelveHoursAgo = now - (12 * 60 * 60 * 1000);
    const oneWeekFromNow = now + (7 * 24 * 60 * 60 * 1000);
    
    return fixturesData.filter(fixture => {
      if (!fixture.start_date) return true;
      
      let gameTime;
      if (fixture.start_date._seconds) {
        gameTime = fixture.start_date._seconds * 1000;
      } else if (typeof fixture.start_date === 'string') {
        gameTime = new Date(fixture.start_date).getTime();
      } else if (fixture.start_date.seconds) {
        gameTime = fixture.start_date.seconds * 1000;
      } else {
        gameTime = new Date(fixture.start_date).getTime();
      }
      
      return gameTime >= twelveHoursAgo && gameTime <= oneWeekFromNow;
    });
  };
  
  // Helper to filter fixtures by user's league preferences
  const filterByUserPreferences = useCallback((fixturesData) => {
    if (!tickerLeagues || tickerLeagues.length === 0) return fixturesData;
    
    return fixturesData.filter(fixture => {
      const fixtureLeague = (fixture.league || '').toLowerCase();
      const fixtureSport = (fixture.sport || '').toLowerCase();
      
      return tickerLeagues.some(pref => {
        const prefLower = pref.toLowerCase();
        // Check if league matches
        if (fixtureLeague.includes(prefLower) || prefLower.includes(fixtureLeague)) return true;
        // Check if sport matches
        if (fixtureSport.includes(prefLower) || prefLower.includes(fixtureSport)) return true;
        // Special cases
        if (prefLower === 'soccer' && (fixtureLeague.includes('premier') || fixtureLeague.includes('la liga') || fixtureLeague.includes('bundesliga') || fixtureLeague.includes('serie a') || fixtureLeague.includes('mls') || fixtureLeague.includes('champions'))) return true;
        if (prefLower === 'tennis' && (fixtureLeague.includes('atp') || fixtureLeague.includes('wta'))) return true;
        if (prefLower === 'mma' && (fixtureLeague.includes('ufc') || fixtureLeague.includes('mma'))) return true;
        return false;
      });
    });
  }, [tickerLeagues]);
  
  // Sort and deduplicate fixtures for the ticker.
  // Shows ALL upcoming games across multiple days (not just next-game-per-team),
  // so tomorrow's and future games are visible alongside today's.
  // Caps at MAX_PER_LEAGUE per league to prevent any single league flooding the ticker.
  const getNextGamePerTeam = useCallback((fixturesData, referenceTimeOverride = null) => {
    // ⭐ Prefer an explicitly-passed reference time (from the API response) over the
    // closure-captured state. This eliminates the race where getNextGamePerTeam runs
    // with a stale/null referenceTime before setReferenceTime() has propagated.
    const ref = referenceTimeOverride || referenceTime;
    const now = ref ? new Date(ref).getTime() : Date.now();
    const threeHoursAgo = now - (3 * 60 * 60 * 1000);
    const MAX_PER_LEAGUE = 20; // Show up to 20 games per league across all days

    const getTime = (f) => {
      if (!f.start_date) return Infinity;
      if (f.start_date._seconds) return f.start_date._seconds * 1000;
      if (f.start_date.seconds) return f.start_date.seconds * 1000;
      return new Date(f.start_date).getTime();
    };

    // Filter out finished games (started > 3 hours ago and not live)
    const active = fixturesData.filter(f => {
      const t = getTime(f);
      return t === Infinity || t >= threeHoursAgo || f.is_live;
    });

    // Sort by start time, soonest first
    active.sort((a, b) => getTime(a) - getTime(b));

    // Deduplicate by fixture_id (server may return dupes across league queries)
    const seenIds = new Set();
    const deduped = active.filter(f => {
      const id = f.fixture_id;
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });

    // Cap per league so NCAAB (300+ games) doesn't dominate
    const leagueCounts = {};
    return deduped.filter(f => {
      const league = (f.league || 'unknown').toLowerCase();
      leagueCounts[league] = (leagueCounts[league] || 0) + 1;
      return leagueCounts[league] <= MAX_PER_LEAGUE;
    });
  }, [referenceTime]);

  // ⭐ Self-heal: if a fetch landed while referenceTime was still null/stale (so
  // getNextGamePerTeam filtered with the real wall clock and dropped frozen games),
  // re-apply the filter against rawFixtures once the frozen reference time arrives.
  // This guarantees the ticker populates even when the closure race would have left
  // it empty — critical for Archive/Freeze Mode.
  useEffect(() => {
    if (!referenceTime || rawFixtures.length === 0) return;
    setFixtures(getNextGamePerTeam(rawFixtures, referenceTime));
    // Intentionally only depends on referenceTime; re-filtering on every rawFixtures
    // change would double-apply the filter on the normal fetch path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceTime]);

  // Progressive chunked loading - show content ASAP, then load in background
  const fetchTickerFixtures = useCallback(async (force = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    
    // Don't refetch if we fetched recently (within 2 minutes) unless forced
    if (!force && lastFetchedRef.current && (Date.now() - lastFetchedRef.current) < 120000) {
      return;
    }
    
    fetchingRef.current = true;
    setError(null);
    
    // ⭐ INSTANT: If we have cached data, don't show loading - just refresh in background
    const hasCachedData = fixtures.length > 0;
    if (!hasCachedData) {
      setLoadProgress(0);
      setIsLoading(true);
    }
    
    const functionsUrl = getFunctionsUrl();
    
    try {
      // Build league params from user preferences
      const leagueParams = tickerLeagues?.length > 0 
        ? `&leagues=${tickerLeagues.join(',')}`
        : '';
      
      // ═══════════════════════════════════════════════════════════════════════════
      // ⭐ SINGLE OPTIMIZED FETCH - Skip multi-phase if we have cached data
      // If no cache: Phase 1 (tiny batch) → Phase 2 → Phase 3
      // If cached: Single fetch for fresh data, update seamlessly
      // ═══════════════════════════════════════════════════════════════════════════
      
      if (hasCachedData) {
        // Background refresh - single fetch, no loading states
        // console.log('[Ticker] Background refresh (have cached data)');
        setIsLoadingMore(true);
        
        const response = await fetch(`${functionsUrl}/getTickerFixtures?limit=200${leagueParams}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.fixtures?.length > 0) {
            setRawFixtures(data.fixtures);
            setReferenceTime(data.reference_time ?? null);
            const nextGamesOnly = getNextGamePerTeam(data.fixtures, data.reference_time ?? null);
            setFixtures(nextGamesOnly);
            saveCachedFixtures(data.fixtures, data.reference_time ?? null); // ⭐ Update cache
            setLoadProgress(3);

            // console.log('[Ticker] Background refresh complete:', nextGamesOnly.length, 'fixtures');
          }
        }
      } else {
        // No cache - do progressive loading
        // PHASE 1: IMMEDIATE - Tiny batch for instant display
        const instantUrl = `${functionsUrl}/getTickerFixtures?limit=10${leagueParams}`;
        
        const instantResponse = await fetch(instantUrl);
        
        if (instantResponse.ok) {
          const instantData = await instantResponse.json();
          if (instantData.success && instantData.fixtures?.length > 0) {
            setRawFixtures(instantData.fixtures);
            setReferenceTime(instantData.reference_time ?? null);
            const nextGamesOnly = getNextGamePerTeam(instantData.fixtures, instantData.reference_time ?? null);
            setFixtures(nextGamesOnly);
            setIsLoading(false); // ⭐ Show ticker IMMEDIATELY!
            setLoadProgress(1);
            saveCachedFixtures(instantData.fixtures, instantData.reference_time ?? null); // ⭐ Cache immediately

          }
        }
        
        // PHASE 2: More fixtures in background
        setIsLoadingMore(true);
        const fullResponse = await fetch(`${functionsUrl}/getTickerFixtures?limit=200${leagueParams}`);
        
        if (fullResponse.ok) {
          const fullData = await fullResponse.json();
          if (fullData.success && fullData.fixtures?.length > 0) {
            setRawFixtures(fullData.fixtures);
            setReferenceTime(fullData.reference_time ?? null);
            const nextGamesOnly = getNextGamePerTeam(fullData.fixtures, fullData.reference_time ?? null);
            setFixtures(nextGamesOnly);
            setLoadProgress(3);
            saveCachedFixtures(fullData.fixtures, fullData.reference_time ?? null); // ⭐ Update cache

            const leagues = [...new Set(nextGamesOnly.map(f => f.league).filter(Boolean))];

            fullFetchDoneRef.current = true;
          }
        }
      }
      
      lastFetchedRef.current = Date.now();
      
    } catch (err) {
      console.error('[Ticker] Fetch error:', err);
      
      // Fallback to getAllCachedFixtures (only if no data at all)
      if (fixtures.length === 0) {
        try {
          const fallbackResponse = await fetch(`${functionsUrl}/getAllCachedFixtures?limit=50&include_odds=false&include_live=true&include_past=true`);
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            if (fallbackData.success && fallbackData.fixtures?.length > 0) {
              setReferenceTime(fallbackData.reference_time ?? null);
              let fixturesData = filterByTimeRelevance(fallbackData.fixtures, fallbackData.reference_time ?? null);
              setRawFixtures(fixturesData);
              const filtered = filterByUserPreferences(fixturesData);
              const nextGamesOnly = getNextGamePerTeam(filtered, fallbackData.reference_time ?? null);
              setFixtures(nextGamesOnly);
              saveCachedFixtures(nextGamesOnly, fallbackData.reference_time ?? null);
              console.log('[Ticker] Fallback loaded', nextGamesOnly.length, 'fixtures');
            }
          }
        } catch (fallbackErr) {
          console.error('[Ticker] Fallback also failed:', fallbackErr);
        }
      }
      
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [tickerLeagues, filterByUserPreferences, getNextGamePerTeam, fixtures.length]);
  
  // Track if initial preferences have loaded
  const prefsLoadedRef = useRef(false);
  
  // Initial fetch - start IMMEDIATELY for fast first paint, don't wait for profile
  useEffect(() => {
    // ⭐ Start fetching immediately using localStorage preferences (already loaded synchronously)
    // User profile preferences will be applied on subsequent refreshes
    fetchTickerFixtures(true);
    prefsLoadedRef.current = true;
  }, []); // Only run on mount
  
  // Fetch when ticker is enabled and no fixtures
  useEffect(() => {
    if (isTickerEnabled && fixtures.length === 0 && !fetchingRef.current && prefsLoadedRef.current) {
      fetchTickerFixtures(true);
    }
  }, [isTickerEnabled, fixtures.length, fetchTickerFixtures]);
  
  // Periodic refresh every 5 minutes when ticker is enabled
  useEffect(() => {
    if (!isTickerEnabled) return;
    
    const interval = setInterval(() => {
      fetchTickerFixtures(true); // Force refresh
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isTickerEnabled, fetchTickerFixtures]);
  
  // Refetch fixtures when ticker preferences change - ALWAYS refetch, don't skip
  useEffect(() => {
    // Check if we need to refetch due to profile sync OR preference changes
    if (needsRefetchRef.current || (prefsLoadedRef.current && tickerLeagues && tickerLeagues.length > 0)) {
      needsRefetchRef.current = false; // Clear the flag
      fetchTickerFixtures(true); // Force refetch with new preferences
    }
  }, [tickerLeagues, fetchTickerFixtures]);
  
  // ⭐ Fetch SGP cache status when setting is enabled and fixtures are loaded.
  // fetchSGPCacheStatus is stable (no deps) so it's safe to omit from deps here.
  useEffect(() => {
    if (!showTickerSGPIndicator || fixtures.length === 0 || isLoading) return;
    // Short delay so this doesn't block the initial render paint
    const timer = setTimeout(() => fetchSGPCacheStatus(fixtures), 800);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTickerSGPIndicator, fixtures, isLoading]);

  // ⭐ Periodically refresh SGP statuses every 20 minutes (SGP cache gets refreshed hourly).
  // Clears localStorage TTL by re-fetching fresh fixture statuses.
  useEffect(() => {
    if (!showTickerSGPIndicator) return;
    const interval = setInterval(() => {
      // Drop all `false` entries so newly-generated SGPs are discovered.
      // Preserve `true` entries (confirmed SGPs) to avoid redundant API calls.
      const pruned = Object.fromEntries(
        Object.entries(sgpCacheStatusRef.current).filter(([, v]) => v === true)
      );
      sgpCacheStatusRef.current = pruned;
      setSgpCacheStatus(pruned);
      sgpFetchingRef.current = false;
      fetchSGPCacheStatus(fixtures);
    }, 20 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTickerSGPIndicator, fixtures]);
  
  // ⭐ Fetch live scores when enabled and fixtures are loaded
  useEffect(() => {
    if (showLiveScores && fixtures.length > 0 && !isLoading) {
      // Initial fetch
      const timer = setTimeout(() => {
        fetchLiveScores(fixtures);
      }, 1500);
      
      // Refresh every 30 seconds for live games
      const interval = setInterval(() => {
        fetchLiveScores(fixtures);
      }, 30 * 1000);
      
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [showLiveScores, fixtures, isLoading, fetchLiveScores]);
  
  // Header height constants
  const NAVBAR_HEIGHT = 56;
  const TICKER_HEIGHT = 40;
  const INJURY_TICKER_HEIGHT = 36; // 🏥 Injury ticker bar
  const TICKER_NAVBAR_GAP = 8; // Space between ticker and navbar
  const TOP_SPACER_HEIGHT = 16; // Space from top when ticker is hidden/collapsed
  
  // Calculate total header height based on current visibility state
  const tickerActuallyVisible = isTickerEnabled && !isTickerCollapsed;
  const injuryTickerActuallyVisible = tickerActuallyVisible && isInjuryTickerEnabled;
  const totalHeaderHeight = tickerActuallyVisible 
    ? NAVBAR_HEIGHT + TICKER_HEIGHT + (injuryTickerActuallyVisible ? INJURY_TICKER_HEIGHT : 0) + TICKER_NAVBAR_GAP
    : NAVBAR_HEIGHT + TOP_SPACER_HEIGHT + TICKER_NAVBAR_GAP;
  
  // Update CSS variable for header height (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--header-height', `${totalHeaderHeight}px`);
      document.documentElement.style.setProperty('--navbar-height', `${NAVBAR_HEIGHT}px`);
      document.documentElement.style.setProperty('--ticker-height', tickerActuallyVisible ? `${TICKER_HEIGHT}px` : `${TOP_SPACER_HEIGHT}px`);
    }
  }, [tickerActuallyVisible, totalHeaderHeight]);
  
  // Toggle collapsed state (chevron on ticker)
  const toggleCollapsed = useCallback(() => {
    setIsTickerCollapsed(prev => !prev);
  }, []);
  
  // Computed: is ticker actually visible right now?
  const isTickerVisible = isTickerEnabled && !isTickerCollapsed;
  
  const value = {
    // State
    isTickerEnabled,      // Default setting (from settings page)
    isTickerCollapsed,    // Session collapse state (from chevron)
    isTickerVisible,      // Computed: actually showing right now
    fixtures,
    referenceTime,        // ⭐ Freeze-mode reference time (ISO string or null)
    isLoading,
    isLoadingMore,        // True when loading additional fixtures in background
    loadProgress,         // 0 = nothing, 1 = initial (10), 2 = second (30), 3 = full (100)
    error,
    lastFetched: lastFetchedRef.current,
    
    // User preferences
    tickerLeagues,        // User's league/sport preferences
    tickerNameFormat,     // Name format: 'full', 'short', or 'abbreviated'
    showTickerSGPIndicator, // ⭐ Whether to show SGP cache indicator
    tickerSGPOnly,        // ⭐ Whether to only show games with cached SGPs
    sgpCacheStatus,       // ⭐ Map of fixture_id -> has cached SGPs
    showLiveScores,       // ⭐ Whether to show live scores in ticker
    liveScores,           // ⭐ Map of fixture_id -> { home_score, away_score, period, clock }
    isInjuryTickerEnabled, // 🏥 Whether injury ticker is shown
    injuryTickerLeagues,  // 🏥 League filter for injury ticker (null = all)
    injuryMinSeverity,    // 🏥 Minimum severity to show ('all' | 'questionable_up' | 'doubtful_up' | 'out_only')
    injuryGroupBy,        // 🏥 How to group injuries: 'fixture' (both teams) | 'team' (one team per card)
    
    // Layout helpers
    navbarHeight: NAVBAR_HEIGHT,
    tickerHeight: TICKER_HEIGHT,
    totalHeaderHeight,
    
    // Actions
    toggleTicker,         // Toggle default setting (settings page)
    enableTicker,
    disableTicker,
    toggleCollapsed,      // Toggle collapse (chevron on ticker)
    expandTicker: () => setIsTickerCollapsed(false),
    collapseTicker: () => setIsTickerCollapsed(true),
    refreshFixtures: () => fetchTickerFixtures(true),
    updateTickerLeagues,  // Update user's league preferences
    updateTickerNameFormat, // Update name format preference
    updateShowTickerSGPIndicator, // ⭐ Toggle SGP indicator setting
    updateTickerSGPOnly,  // ⭐ Toggle SGP-only mode
    updateShowLiveScores, // ⭐ Toggle live scores in ticker
    updateInjuryTickerEnabled, // 🏥 Toggle injury ticker
    updateInjuryTickerLeagues, // 🏥 Update injury ticker league filter
    updateInjuryMinSeverity,  // 🏥 Update injury ticker severity filter
    updateInjuryGroupBy,      // 🏥 Update injury ticker group-by setting
  };
  
  return (
    <TickerContext.Provider value={value}>
      {children}
    </TickerContext.Provider>
  );
};

export default TickerContext;

